import type { FunctionComponent, ReactInstance, ReactNode } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { ErrorBoundary } from '@trello/error-boundaries';
import { useFeatureGate } from '@trello/feature-gate-client';
import { intl } from '@trello/i18n';
import type { IterableType } from '@trello/infinite-list';
import { LegacyInfiniteList } from '@trello/infinite-list';
import { buildFuzzyMatcher } from '@trello/strings';
import type { TeamTestIds } from '@trello/test-ids';
import { getTestId } from '@trello/test-ids';

import type { Member } from 'app/scripts/models/Member';
import { WorkspaceMembersFilterBox } from 'app/src/components/WorkspaceMembersFilterBox';
import { WorkspaceCollaboratorItem } from './WorkspaceCollaboratorItem';
import type { WorkspaceCollaboratorsFragment } from './WorkspaceCollaboratorsFragment.generated';
import { useWorkspaceCollaboratorsFragment } from './WorkspaceCollaboratorsFragment.generated';
import { WorkspaceMemberItem } from './WorkspaceMemberItem';
import { useWorkspaceMembersListQuery } from './WorkspaceMembersListQuery.generated';

import styles from './WorkspaceMembersList.less';

// eslint-disable-next-line @trello/assets-alongside-implementation
import missingSvg from 'resources/images/organization/missing.svg';

type Collaborator =
  NonNullable<WorkspaceCollaboratorsFragment>['collaborators'][0];

export interface ListMember extends Member {
  allowReactivate: boolean;
  allowTypeChange: boolean;
  canRemove: boolean;
  idBoards: string[];
  isMe: boolean;
  isOrganizationAdmin: boolean;
  isOrgMember: boolean;
  isPending: boolean;
  orgIsBC: boolean;
  orgMemberType: string;
  ownsOrg: boolean;
  viewTitle: string;
}

interface WorkspaceMembersListProps {
  collaboratorIds?: string[];
  memberItems: ListMember[];
  memberTypeFilter: string;
  orgBillingUrl?: string;
  orgIdEnterprise?: string;
  orgEnterpriseName?: string;
  workspaceId: string;
}

export const WorkspaceMembersList: FunctionComponent<
  WorkspaceMembersListProps
> = ({
  collaboratorIds,
  memberItems,
  memberTypeFilter,
  orgBillingUrl,
  orgIdEnterprise,
  orgEnterpriseName,
  workspaceId,
}) => {
  const [nameFilter, setNameFilter] = useState('');

  const { data: org } = useWorkspaceCollaboratorsFragment({
    from: { id: workspaceId },
    optimistic: true,
  });

  const { data: lastActiveData } = useWorkspaceMembersListQuery({
    variables: { idOrganization: workspaceId },
    waitOn: ['None'],
  });

  const isGuestList = memberTypeFilter === 'guest';

  const itemsRenderer = useCallback(
    (items: IterableType, ref: (instance: ReactInstance) => void) => (
      <div
        className={styles.orgMembersList}
        data-testid={getTestId<TeamTestIds>('workspace-member-list')}
        ref={(div: HTMLDivElement) => ref(div)}
      >
        {items as ReactNode}
      </div>
    ),

    [],
  );

  const isVisibleMember = useCallback(
    (member: Collaborator | Member) => {
      return (
        !nameFilter || buildFuzzyMatcher(nameFilter)(member.fullName ?? '')
      );
    },
    [nameFilter],
  );

  const visibleMemberItems = useMemo(
    () => memberItems?.filter((member) => isVisibleMember(member)) || [],
    [memberItems, isVisibleMember],
  );

  const visibleCollaboratorItems = useMemo(
    () =>
      org?.collaborators?.filter(
        (member) =>
          collaboratorIds?.includes(member.id) && isVisibleMember(member),
      ) || [],
    [collaboratorIds, org?.collaborators, isVisibleMember],
  );

  const itemCount = isGuestList
    ? visibleCollaboratorItems.length
    : visibleMemberItems.length;

  const renderMember = useCallback(
    (index: number) => {
      const member = visibleMemberItems[index];

      const memberOfOrg = lastActiveData?.organization?.members.find(
        (orgMember) => orgMember.id === member.id,
      );

      return (
        <WorkspaceMemberItem
          key={member.id}
          allowReactivate={member.allowReactivate}
          allowTypeChange={member.allowTypeChange}
          fullName={member.fullName}
          id={member.id}
          idEnterprise={member.idEnterprise}
          isDeactivated={member.isDeactivated}
          isMe={member.isMe}
          isOrganizationAdmin={member.isOrganizationAdmin}
          isOrgMember={member.isOrgMember}
          isPending={member.isPending}
          lastActive={memberOfOrg?.lastActive}
          orgBillingUrl={orgBillingUrl}
          orgEnterpriseName={orgEnterpriseName}
          orgIdEnterprise={orgIdEnterprise}
          orgIsBC={member.orgIsBC}
          orgMemberType={member.orgMemberType}
          ownsOrg={member.ownsOrg}
          username={member.username}
          workspaceId={workspaceId}
        />
      );
    },
    [
      visibleMemberItems,
      lastActiveData?.organization?.members,
      orgBillingUrl,
      orgEnterpriseName,
      orgIdEnterprise,
      workspaceId,
    ],
  );

  const renderCollaborator = useCallback(
    (index: number) => {
      const member = visibleCollaboratorItems[index];
      return (
        member && (
          <WorkspaceCollaboratorItem
            key={member.id}
            idMember={member.id}
            idOrganization={workspaceId}
          />
        )
      );
    },
    [visibleCollaboratorItems, workspaceId],
  );

  const { value: shouldUseNewInfiniteList } = useFeatureGate(
    'tplat_new_infinite_list',
  );

  const renderItem = isGuestList ? renderCollaborator : renderMember;

  const renderAll = () => {
    const items = [];
    for (let i = 0; i < itemCount; i++) {
      items.push(renderItem(i));
    }
    return items;
  };

  return (
    <ErrorBoundary
      tags={{
        ownershipArea: 'trello-web-eng',
        feature: 'Organization Members List Component',
      }}
    >
      <WorkspaceMembersFilterBox onNameFilterChange={setNameFilter} />
      {!itemCount ? (
        <div className={styles.orgMembersMissingContainer}>
          <img
            alt={intl.formatMessage({
              id: 'templates.org_members.no-guest-result',
              defaultMessage: "Uh oh, there's no one here by that name.",
              description: 'Alt text for the missing guests image',
            })}
            className={styles.orgMembersMissingIllustration}
            src={missingSvg}
          />
          <div className={styles.orgMembersContent}>
            {memberTypeFilter === 'guest' ? (
              <FormattedMessage
                id="templates.org_members.no-guest-result"
                defaultMessage="Uh oh, there's no one here by that name."
                description="No guests found in the workspace"
              />
            ) : (
              <FormattedMessage
                id="templates.org_members.no-member-result"
                defaultMessage="Uh oh, there's no one here by that name. Should there be? Invite them now!"
                description="No members found in the workspace. You should invite them now"
              />
            )}
          </div>
        </div>
      ) : shouldUseNewInfiniteList ? (
        <div
          className={styles.orgMembersList}
          data-testid={getTestId<TeamTestIds>('workspace-member-list')}
        >
          {renderAll()}
        </div>
      ) : (
        <LegacyInfiniteList
          itemCount={itemCount}
          itemsRenderer={itemsRenderer}
          awaitMore={false}
          pageSize={100}
          renderItem={renderItem}
        />
      )}
    </ErrorBoundary>
  );
};
